import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/constants';
import { activationProps } from '../common/activationProps';
import { clientToCssPixels, readViewportCssSize } from '../../utils/displaySize';
import { TimerAssistant } from '../icons';

const DraggableCircle = styled.div`
  position: fixed;
  bottom: 50%;
  right: 0;
  width: 60px;
  height: 60px;
  z-index: 1000;
  user-select: none;
  transform: translate(-17%, -50%);
  cursor: grab;
  pointer-events: auto;
  .icon,
  svg {
    display: block;
    width: 100%;
    height: 100%;
  }
`;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const pinToViewport = (el: HTMLDivElement, x: number, y: number) => {
  el.style.left = `${x}px`;
  el.style.top = `${y}px`;
  el.style.right = 'auto';
  el.style.bottom = 'auto';
  // The resting transform would shift the painted circle outside the clamped bounds
  el.style.transform = 'none';
};

const FloatingTimer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const timerRef = useRef<HTMLDivElement>(null);
  const pos = useRef({ x: 0, y: 0, dx: 0, dy: 0 });
  const isDragging = useRef(false);

  const handleMouseDown = (e: React.MouseEvent) => {
    const el = timerRef.current;
    if (!el) return;

    isDragging.current = false;

    const { clientX, clientY } = e;
    const { left, top } = el.getBoundingClientRect();
    pos.current = {
      x: clientX,
      y: clientY,
      dx: left,
      dy: top,
    };

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - pos.current.x;
      const deltaY = moveEvent.clientY - pos.current.y;

      if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
        isDragging.current = true;
      }

      const el = timerRef.current;
      if (!el) return;

      const newX = clientToCssPixels(pos.current.dx + deltaX);
      const newY = clientToCssPixels(pos.current.dy + deltaY);
      const viewport = readViewportCssSize();

      const clampedX = clamp(newX, 0, viewport.width - el.offsetWidth);
      const clampedY = clamp(newY, 0, viewport.height - el.offsetHeight);

      pinToViewport(el, clampedX, clampedY);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleClick = () => {
    if (isDragging.current) return;
    navigate(ROUTES.TIMER_LIST);
  };

  return (
    <DraggableCircle
      data-testid="floating-timer"
      data-floating-action
      ref={timerRef}
      onMouseDown={handleMouseDown}
      aria-label={t('common:timers.timer')}
      {...activationProps(handleClick)}
    >
      <TimerAssistant />
    </DraggableCircle>
  );
};

export default FloatingTimer;
